import { useEffect, useRef, useState } from 'react';
import BrandLogo from '@/components/BrandLogo';
import StandaloneSurface from '@/components/Layout/StandaloneSurface';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AppIcon } from '@/components/ui/icons';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/features/ui/components/Button';

interface MfaChallengeFormProps {
  onVerify: (code: string) => Promise<void>;
  onCancel?: () => void;
  error?: string;
  isVerifying?: boolean;
  userEmail?: string;
}

/** Provider-backed TOTP challenge. Product routes unlock only after Supabase
 * reports an AAL2 session; this component never infers verification locally. */
const MfaChallengeForm = ({
  onVerify,
  onCancel,
  error,
  isVerifying = false,
  userEmail,
}: MfaChallengeFormProps) => {
  const [code, setCode] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (code.length !== 6 || isVerifying) return;
    await onVerify(code);
  };

  return (
    <StandaloneSurface measure="reading">
      <div className="space-y-6">
        <div className="text-center">
          <BrandLogo variant="auth" className="mx-auto mb-3" />
          <p className="text-sm text-text-muted">Secure session verification</p>
        </div>

        <Card variant="elevated">
          <CardHeader className="items-center text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-primary/10">
              <AppIcon name="shield" className="h-5 w-5 text-accent-primary" />
            </div>
            <CardTitle className="mt-3">Two-factor authentication</CardTitle>
            <CardDescription>
              Enter the current six-digit code from your authenticator app.
            </CardDescription>
            {userEmail && <p className="text-sm font-medium text-text-secondary">{userEmail}</p>}
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mfa-code">Verification code</Label>
                <Input
                  ref={inputRef}
                  id="mfa-code"
                  name="mfa-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  enterKeyHint="done"
                  maxLength={6}
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={isVerifying}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? 'mfa-error' : 'mfa-code-help'}
                  className="h-12 text-center font-mono text-xl tracking-[0.35em]"
                  placeholder="000000"
                />
                <p id="mfa-code-help" className="text-xs text-text-muted">
                  Codes refresh every 30 seconds. Pasting a code is supported.
                </p>
              </div>

              {error && (
                <Alert id="mfa-error" variant="destructive" aria-live="polite">
                  <AppIcon name="alertCircle" className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                size="lg"
                fullWidth
                loading={isVerifying}
                disabled={code.length !== 6}
                startIcon={<AppIcon name="shield" className="h-4 w-4" />}
              >
                Verify secure session
              </Button>
              {onCancel && (
                <Button type="button" variant="ghost" fullWidth onClick={onCancel}>
                  Sign out and use another account
                </Button>
              )}
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-text-muted">
          Lost access to your authenticator?{' '}
          <a href="mailto:support@ax1.capital" className="font-medium text-accent-primary hover:underline">
            Contact support
          </a>
        </p>
      </div>
    </StandaloneSurface>
  );
};

export default MfaChallengeForm;
